import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';

import {Box, Divider, Paper, Stack, Typography, useTheme} from '@mui/material';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';

import {useGetUnitsWithConcessionsQuery} from '../../../../../features/coreApi/metricsSlice';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../../../../app/store/reducers/session';
import {ConcessionsColumns, ConcessionsMainColumns, valueGetter} from '../../../../constants/tableColumns';
import {sortDayjsDates, sortStrings, sortNumbers, sortBooleans} from '../../../../utils/sorting';
import {getDashboardCommunities} from '../../../../../app/store/reducers/dashboard';
import {CustomNoRowsOverlay, ExportToolBarWithSearch} from '../../../@extended/DataGridTables';
import {ComplexLoadingBox} from '../../../@extended/LoadingBox';
import {StyledTable} from '../../../@extended/CustomTables';
import ErrorBox from '../../../@extended/ErrorBox';
import TableBodyCell from '../../../@extended/TableBodyCell';
import D3GroupedBarChart from '../../../@extended/D3GroupedBarChart';

const UnitsWithConcessionsGraph = ({data}) => {
    const exitdata = useMemo(() => {
        const monthlyData = [];

        for (const key in data) {
            const propertyData = data[key];

            for (const month in propertyData) {
                const monthData = propertyData[month];
                const formattedMonth = dayjs(month).format("MMM'YY");
                const obj = {
                    month: formattedMonth,
                    dayjsDate: dayjs(month),
                    exits: monthData.exits
                };

                monthlyData.push(obj);
            }
        }

        return monthlyData.sort((a, b) => sortDayjsDates(a.dayjsDate, b.dayjsDate, false));
    }, [data]);

    const filteredData = useMemo(() => {
        const monthlyData = {};

        for (const key in data) {
            const propertyData = data[key];

            for (const month in propertyData) {
                const monthData = propertyData[month];
                const formattedMonth = dayjs(month).format('YYYY-MM');

                // Ensure the month exists in monthlyData
                if (!monthlyData[formattedMonth]) {
                    monthlyData[formattedMonth] = [];
                }

                // Process traditional concessions
                monthData.concessions.forEach((c, index) => {
                    const cObj = {
                        month: formattedMonth,
                        building_name: monthData.building_name,
                        concessions: c?.concessions || '',
                        total_percent: c?.concession_percent?.total_percent || 0,
                        type: 'Traditional Concession',
                        id: c.id
                    };

                    monthlyData[formattedMonth].push(cObj);
                });

                // Process stake offers
                monthData.stake_offers.forEach((c, index) => {
                    const cObj = {
                        month: formattedMonth,
                        building_name: monthData.building_name,
                        concessions: c?.concessions || '',
                        total_percent: c?.concession_percent?.total_percent || 0,
                        type: 'Stake+ Offer',
                        id: c.id
                    };

                    monthlyData[formattedMonth].push(cObj);
                });
            }
        }

        return monthlyData;
    }, [data]);

    const graphData = useMemo(() => {
        const graphData = [];

        for (const month in filteredData) {
            const monthObj = {
                month: dayjs(month).format("MMM'YY"),
                dayjsDate: dayjs(month),
                values: []
            };

            for (let i = 0; i < filteredData[month].length; i++) {
                const obj = filteredData[month][i];
                const keyString = obj.id;
                const percent = obj.total_percent;
                monthObj.values.push({
                    key: keyString,
                    value: percent,
                    text: obj.concessions,
                    color: obj.type === 'Stake+ Offer' ? '#149CE0' : '#FEE3A0'
                });
            }

            graphData.push(monthObj);
        }

        return graphData.sort((a, b) => sortDayjsDates(a.dayjsDate, b.dayjsDate, false));
    }, [filteredData]);

    return <D3GroupedBarChart data={graphData} exitData={exitdata} />;
};

const ComparisonPanel = ({row}) => {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const fileName = `concessions`;

    // Define columns for the comparison units table
    const columns = useMemo(() => {
        return [...ConcessionsColumns].map((column) => ({
            ...column,
            field: column.id,
            headerName: column.label,
            renderCell: (params) => <TableBodyCell column={column} params={params} />,
            valueFormatter: (params) => valueGetter(params, column),
            renderEditCell: (params) => <TableBodyCell column={column} params={params} />,
            sortComparator: column?.numeric
                ? (v1, v2) => sortNumbers(v1, v2)
                : column?.boolean
                  ? (v1, v2) => sortBooleans(v1, v2)
                  : column?.dataFormat == 'date'
                    ? (v1, v2) => sortDayjsDates(v1, v2)
                    : (v1, v2) => sortStrings(v1, v2)
        }));
    }, []);

    const rows = useMemo(() => {
        const allRows = [];

        for (const c of row.concessions) {
            const cObj = {
                concessions: c?.concessions,
                type: c?.type,
                id: c?.id,
                from_date: c?.from_date,
                to_date: c?.to_date,
                total_percent: c?.concession_percent?.total_percent,
                building_name: c?.name
            };
            allRows.push(cObj);
        }

        for (const c of row.stake_offers) {
            const cObj = {
                concessions: c?.concessions,
                type: c?.type,
                id: c?.id,
                from_date: c?.from_date,
                to_date: c?.to_date,
                total_percent: c?.concession_percent?.total_percent,
                building_name: c?.name,
                building_id: c?.building_id
            };
            allRows.push(cObj);
        }

        return allRows.sort((a, b) => sortStrings(a.building_name, b.building_name));
    }, [row]);

    const handlePaginationModelChange = (newModel) => {
        setPage(newModel.page);
        setPageSize(newModel.pageSize);
    };

    return (
        <Box sx={{width: '100%', p: 0, bgcolor: theme.palette.grey[100]}}>
            <StyledTable
                columns={columns}
                rows={[...rows]}
                autoHeight
                disableColumnMenu
                disableColumnSelector
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                pagination
                paginationModel={{
                    page: page,
                    pageSize: pageSize
                }}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 20, 50, 100]}
                slots={{
                    toolbar: () => <ExportToolBarWithSearch defaultFilename={fileName} />,
                    noRowsOverlay: () => <CustomNoRowsOverlay msg={'No comparable concessions found'} />
                }}
                sx={{
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        borderRadius: 0
                    },
                    '& .MuiDataGrid-cell': {
                        bgcolor: theme.palette.grey[50],
                        borderBottom: `1px solid ${theme.palette.grey[100]}`,
                        py: 1,
                        p: '8px 16px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        maxWidth: '100%',
                        lineHeight: '1.5',
                        display: 'block'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        bgcolor: theme.palette.grey[100],
                        borderBottom: 'none'
                    },
                    '& .MuiDataGrid-columnHeader': {
                        whiteSpace: 'normal !important',
                        wordWrap: 'break-word !important',
                        lineHeight: '1.5 !important',
                        padding: '8px 16px !important',
                        height: 'auto !important',
                        minHeight: '52px !important'
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        whiteSpace: 'normal !important',
                        wordWrap: 'break-word !important',
                        lineHeight: '1.5 !important',
                        overflow: 'visible !important',
                        textOverflow: 'unset !important'
                    }
                }}
                getRowId={(row) => row.id || `${row.building_id}-${row.unit_name}`}
            />
        </Box>
    );
};

ComparisonPanel.propTypes = {
    row: PropTypes.object
};

const UnitsWithConcessionsTable = ({allData}) => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);
    const communities = useSelector(getDashboardCommunities);
    const [expandedRowsSet, setExpandedRowsSet] = useState(new Set());
    const fileName = 'Lease Signings with Concessions';

    const data = useMemo(() => {
        const obj = {};
        for (const key in allData) {
            if (isStakeAdmin || isAdministrator) {
                const cSfids = params?.community_sfid ? [params.community_sfid] : communities.map((c) => c.sfid);
                if (cSfids.includes(key)) {
                    obj[key] = allData[key];
                }
            } else {
                if (propertyPermissions.includes(key)) {
                    obj[key] = allData[key];
                }
            }
        }

        return obj;
    }, [allData, isStakeAdmin, isAdministrator, propertyPermissions, communities, params]);

    const mainRows = useMemo(() => {
        const months_obj = {};

        for (const sfid in data) {
            const month_data = data[sfid];
            for (const month in month_data) {
                const concessions = month_data[month]?.concessions.map((c) => ({...c, type: 'Traditional Concession'}));
                const stake_offers = month_data[month]?.stake_offers.map((c) => ({...c, type: 'Stake+ Offer'}));
                const obj = {
                    property_sfid: sfid,
                    month: dayjs(month).format('MMMM YYYY'),
                    concessions: concessions,
                    stake_offers: stake_offers,
                    exits: month_data[month]?.exits || 0,
                    concessions_count: month_data[month]?.concessions_count || 0,
                    stake_offers_count: month_data[month]?.stake_offers_count || 0,
                    building_name: month_data[month]?.building_name || '',
                    id: `${sfid}_${month}`
                };

                if (!months_obj[month]) {
                    months_obj[month] = [obj];
                } else {
                    months_obj[month].push(obj);
                }
            }
        }

        const mainRows = [];
        for (const month in months_obj) {
            const mObj = {
                month: dayjs(month),
                concessions_count: months_obj[month].reduce((acc, curr) => acc + curr.concessions_count, 0),
                stake_offers_count: months_obj[month].reduce((acc, curr) => acc + curr.stake_offers_count, 0),
                exits: months_obj[month].reduce((acc, curr) => acc + curr.exits, 0),
                concessions: months_obj[month].map((c) => c.concessions).flat(),
                stake_offers: months_obj[month].map((c) => c.stake_offers).flat(),
                id: `${month}`
            };
            mainRows.push(mObj);
        }

        return mainRows.sort((a, b) => sortDayjsDates(a.month, b.month, true));
    }, [data]);

    const columns = ConcessionsMainColumns.map((column) => ({
        ...column,
        field: column.id,
        headerName: column.label,
        renderCell: (params) => <TableBodyCell column={column} params={params} />,
        valueFormatter: (params) => valueGetter(params, column),
        renderEditCell: (params) => <TableBodyCell column={column} params={params} />,
        sortComparator: column?.numeric
            ? (v1, v2) => sortNumbers(v1, v2)
            : column?.boolean
              ? (v1, v2) => sortBooleans(v1, v2)
              : column?.dataFormat == 'month'
                ? (v1, v2) => sortDayjsDates(v1, v2)
                : (v1, v2) => sortStrings(v1, v2)
    }));

    const getDetailPanelContent = (params) => {
        return <ComparisonPanel row={params.row} />;
    };

    return (
        <Stack direction={'column'} divider={<Divider />}>
            <Stack direction="column" sx={{width: '100%', p: 2}}>
                <Typography variant="h5" fontWeight={'fontWeightMedium'}>
                    Concession Performance [BETA]
                </Typography>
                <Typography variant="body1" color={'text.subtitle'}>
                    Visualize how your concession offers impacted the number of pending/signed leases.
                </Typography>
            </Stack>
            {params?.community_sfid && <UnitsWithConcessionsGraph data={data} />}
            <div style={{minHeight: 100, height: '100%', width: '100%'}}>
                <StyledTable
                    columns={columns}
                    rows={[...mainRows]}
                    onPaginationModelChange={() => {
                        setExpandedRowsSet(new Set());
                    }}
                    disableExtendRowFullWidth={true}
                    disableVirtualization={true}
                    pagination
                    pageSizeOptions={[10, 20, 50, 100]}
                    slots={{
                        toolbar: () => <ExportToolBarWithSearch defaultFilename={fileName} />,
                        noRowsOverlay: () => <CustomNoRowsOverlay msg={'There is no lease signings with concessions data'} />,
                        detailPanelExpandIcon: ExpandMoreOutlined,
                        detailPanelCollapseIcon: ExpandLessOutlined
                    }}
                    initialState={{
                        pagination: {paginationModel: {pageSize: 10}}
                    }}
                    autoHeight
                    density="standard"
                    rowHeight={40}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={() => 'auto'}
                    detailPanelExpandedRowIds={Array.from(expandedRowsSet)}
                    onDetailPanelExpandedRowIdsChange={(ids) => {
                        setExpandedRowsSet(new Set(ids));
                    }}
                    sx={{
                        '& .MuiIconButton-root': {
                            fontSize: '1.25rem'
                        }
                    }}
                />
            </div>
        </Stack>
    );
};

const UnitsWithConcessions = () => {
    const params = useParams();

    const {data, isLoading, isFetching, isError} = useGetUnitsWithConcessionsQuery(params?.account_sfid, {
        skip: !params?.account_sfid
    });

    const mainComponent = useMemo(() => {
        if (isLoading || isFetching) {
            return <ComplexLoadingBox isLoading={isLoading || isFetching} />;
        }

        if (isError) {
            return <ErrorBox msg={'Error fetching unit with concessions data'} />;
        }

        return <UnitsWithConcessionsTable allData={data} />;
    }, [isLoading, isFetching, isError, data]);

    return <Paper sx={{p: 0, borderRadius: 2, boxShadow: 'none'}}>{mainComponent}</Paper>;
};

export default UnitsWithConcessions;
